import { Alert, Box, Container, Snackbar, Typography } from '@mui/material';
import React, { useState } from 'react';
import ClientHeader from '../ClientHeader/ClientHeader';
import CommunicationSection from './components/CommunicationSection';
import LanguageSection from './components/LanguageSection';
import NotificationsSection from './components/NotificationsSection';
import PrivacySection from './components/PrivacySection';
import SecuritySection from './components/SecuritySection';

import { useNavigate } from 'react-router-dom';
import {
  ClientEmailNotifications,
  ClientPushNotifications,
  ClientSmsNotifications,
  CommunicationSettings,
  LanguageSettings,
  PrivacySettings,
  SecuritySettings,
} from 'shared/types';
import { useDeleteClientProfile } from '../../../hooks/user/useDeleteClientProfile';
import AccountManagementSection from './components/AccountManagementSection';

// ─── Mock initial state ───────────────────────────────────────────────────────

const initialClientEmailNotifications: ClientEmailNotifications = {
  bookingRequests: true,
  confirmations: true,
  reminders: true,
  messages: true,
  receipts: true,
  offers: false,
};

const initialClientPushNotifications: ClientPushNotifications = {
  messages: true,
  bookings: true,
  reminders: true,
};

const initialClientSmsNotifications: ClientSmsNotifications = {
  criticalOnly: false,
};

const initialPrivacySettings: PrivacySettings = {
  profileVisibility: 'public',
  showInSearch: true,
  bookingHistory: 'all',
  showLastActive: true,
  phoneVisibility: 'after-booking',
};

const initialSecuritySettings: SecuritySettings = {
  twoFactorEnabled: false,
  loginAlerts: true,
  activeSessions: [
    { id: '1', device: 'Chrome on Mac', current: true },
    { id: '2', device: 'iPad', current: false, lastActive: '2 days ago' },
  ],
};

const initialCommunicationSettings: CommunicationSettings = {
  preferredMethod: 'messaging',
  responseTime: '4-hours',
  autoReply: false,
  autoReplyMessage: '',
  autoDecline: false,
  requirePetInfo: true,
};

const initialLanguageSettings: LanguageSettings = {
  language: 'en-US',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12-hour',
  currency: 'USD',
  timezone: 'auto',
};

// ─── Component ────────────────────────────────────────────────────────────────

const ClientSettings: React.FC = () => {
  const navigate = useNavigate();

  const { mutate: deleteClientProfile, isPending } = useDeleteClientProfile();

  const [emailNotifications, setClientEmailNotifications] = useState<ClientEmailNotifications>(
    initialClientEmailNotifications
  );
  const [pushNotifications, setClientPushNotifications] = useState<ClientPushNotifications>(
    initialClientPushNotifications
  );
  const [smsNotifications, setClientSmsNotifications] = useState<ClientSmsNotifications>(
    initialClientSmsNotifications
  );
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>(initialPrivacySettings);
  const [securitySettings, setSecuritySettings] =
    useState<SecuritySettings>(initialSecuritySettings);
  const [communicationSettings, setCommunicationSettings] = useState<CommunicationSettings>(
    initialCommunicationSettings
  );
  const [languageSettings, setLanguageSettings] =
    useState<LanguageSettings>(initialLanguageSettings);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const autoSave = () => setSnackbarOpen(true);

  // ── Notification handlers ──
  const handleEmailChange = (key: keyof ClientEmailNotifications, value: boolean) => {
    setClientEmailNotifications((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  const handlePushChange = (key: keyof ClientPushNotifications, value: boolean) => {
    setClientPushNotifications((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  const handleSmsChange = (key: keyof ClientSmsNotifications, value: boolean) => {
    setClientSmsNotifications((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  // ── Privacy handlers ──
  const handlePrivacyChange = (key: keyof PrivacySettings, value: string | boolean) => {
    setPrivacySettings((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  // ── Security handlers ──
  const handleToggle2FA = () => {
    setSecuritySettings((prev) => ({
      ...prev,
      twoFactorEnabled: !prev.twoFactorEnabled,
    }));
    autoSave();
  };

  const handleLoginAlertChange = (value: boolean) => {
    setSecuritySettings((prev) => ({ ...prev, loginAlerts: value }));
    autoSave();
  };

  const handleLogoutSession = (id: string) => {
    setSecuritySettings((prev) => ({
      ...prev,
      activeSessions: prev.activeSessions.filter((s) => s.id !== id),
    }));
    autoSave();
  };

  const handleLogoutAllSessions = () => {
    setSecuritySettings((prev) => ({
      ...prev,
      activeSessions: prev.activeSessions.filter((s) => s.current),
    }));
    autoSave();
  };

  // ── Communication handlers ──
  const handleCommunicationChange = (key: keyof CommunicationSettings, value: string | boolean) => {
    setCommunicationSettings((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  // ── Language handlers ──
  const handleLanguageChange = (key: keyof LanguageSettings, value: string) => {
    setLanguageSettings((prev) => ({ ...prev, [key]: value }));
    autoSave();
  };

  // ── Account management ──
  const handleExportData = () => {
    console.log('Data export requested');
  };

  const handleDeactivate = () => {
    console.log('Account deactivation requested');
    // TODO: call API, then navigate to login
  };

  const handleDelete = () => {
    deleteClientProfile(undefined, {
      onSuccess: () => {
        navigate('/register');
      },
      onError: (error) => {
        console.log(error);
        // show error toast
      },
    });
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <ClientHeader />

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your app preferences and privacy
          </Typography>
        </Box>

        <NotificationsSection
          emailNotifications={emailNotifications}
          pushNotifications={pushNotifications}
          smsNotifications={smsNotifications}
          onEmailChange={handleEmailChange}
          onPushChange={handlePushChange}
          onSmsChange={handleSmsChange}
        />

        <PrivacySection settings={privacySettings} onChange={handlePrivacyChange} />

        <SecuritySection
          settings={securitySettings}
          onToggle2FA={handleToggle2FA}
          onLoginAlertChange={handleLoginAlertChange}
          onLogoutSession={handleLogoutSession}
          onLogoutAllSessions={handleLogoutAllSessions}
        />

        <CommunicationSection
          settings={communicationSettings}
          onChange={handleCommunicationChange}
        />

        <LanguageSection settings={languageSettings} onChange={handleLanguageChange} />

        <AccountManagementSection
          onExportData={handleExportData}
          onDeactivate={handleDeactivate}
          onDelete={handleDelete}
        />
      </Container>

      {/* Auto-save Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)} sx={{ minWidth: 200 }}>
          Settings saved
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientSettings;
